import { useEffect, useState } from 'react';
import { Sparkles } from '../components/icons';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import { authService } from '../services/authService';
import { useAuth } from '../store/authContext';
import { formatPhoneMask, normalizePhone } from '../utils/phone';

const translateProfileError = (message: string) => {
  const raw = message.trim();
  const lower = raw.toLowerCase();

  if (lower.includes('users_phone_unique') || (lower.includes('duplicate key') && lower.includes('phone'))) {
    return 'Este telefone já está em uso por outra conta.';
  }
  if (lower.includes('users_email_unique') || (lower.includes('duplicate key') && lower.includes('email'))) {
    return 'Este email já está em uso por outra conta.';
  }
  if (lower.includes('error in workflow') || lower.includes('workflow error')) {
    return 'Ocorreu um erro interno ao processar sua solicitação. Tente novamente em instantes.';
  }
  if (lower.includes('failed to fetch') || lower.includes('network')) {
    return 'Não foi possível conectar ao servidor. Tente novamente.';
  }
  if (lower.includes('sessão inválida') || lower.includes('sessao invalida')) {
    return 'Sua sessão expirou. Faça login novamente.';
  }
  return raw || 'Não foi possível concluir a operação.';
};

export default function PerfilPage() {
  const { session, logout, updateProfile, loading } = useAuth();
  const [name, setName] = useState(session?.user.name || '');
  const [email, setEmail] = useState(session?.user.email || '');
  const [phone, setPhone] = useState(session?.user.phone || '');
  const [original, setOriginal] = useState({
    name: session?.user.name || '',
    email: session?.user.email || '',
    phone: session?.user.phone || ''
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    if (!session?.user.id) return;
    let active = true;

    const loadProfile = async () => {
      setLoadingProfile(true);
      setError('');
      try {
        const profile = await authService.getUserProfile(session.user.id);
        if (!active) return;
        setName(profile.name);
        setEmail(profile.email);
        setPhone(formatPhoneMask(profile.phone));
        setOriginal({
          name: profile.name,
          email: profile.email,
          phone: profile.phone
        });
      } catch (e) {
        if (!active) return;
        setError(translateProfileError(e instanceof Error ? e.message : 'Falha ao carregar perfil.'));
      } finally {
        if (active) setLoadingProfile(false);
      }
    };

    loadProfile();

    return () => {
      active = false;
    };
  }, [session?.user.id]);

  const save = async () => {
    setError('');

    const nextName = name.trim();
    const nextEmail = email.trim().toLowerCase();
    const rawPhone = phone.trim();
    const prevName = original.name.trim();
    const prevEmail = original.email.trim().toLowerCase();
    const prevPhone = original.phone.trim();
    const prevPhoneNormalized = normalizePhone(prevPhone);
    const nextPhoneNormalized = normalizePhone(rawPhone);

    const nameChanged = nextName !== prevName;
    const emailChanged = nextEmail !== prevEmail;
    const phoneChanged = nextPhoneNormalized !== prevPhoneNormalized;

    let normalizedPhone: string | null = null;
    if (phoneChanged) {
      normalizedPhone = nextPhoneNormalized;
      if (!normalizedPhone) {
        setError('Telefone inválido (use BR/PT).');
        return;
      }
    }

    try {
      const payload: { name?: string; email?: string; phone?: string; password?: string } = {};
      if (nameChanged) payload.name = nextName;
      if (emailChanged) payload.email = nextEmail;
      if (phoneChanged && normalizedPhone) payload.phone = normalizedPhone;
      if (password.trim()) payload.password = password.trim();

      await updateProfile(payload);

      setOriginal({
        name: nameChanged ? nextName : prevName,
        email: emailChanged ? nextEmail : prevEmail,
        phone: phoneChanged && normalizedPhone ? normalizedPhone : prevPhone
      });
      if (phoneChanged && normalizedPhone) setPhone(formatPhoneMask(normalizedPhone));
      setPassword('');
    } catch (e) {
      setError(translateProfileError(e instanceof Error ? e.message : 'Falha ao salvar.'));
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">Perfil</h1>
      <Card className="space-y-3">
        {loadingProfile ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
              <Sparkles className="h-4 w-4 animate-spin" />
              Carregando seus dados de perfil...
            </div>
            <div className="h-12 animate-pulse rounded-xl bg-slate-200/80 dark:bg-slate-700/70" />
            <div className="h-12 animate-pulse rounded-xl bg-slate-200/80 dark:bg-slate-700/70" />
            <div className="h-12 animate-pulse rounded-xl bg-slate-200/80 dark:bg-slate-700/70" />
            <div className="h-12 animate-pulse rounded-xl bg-slate-200/80 dark:bg-slate-700/70" />
          </div>
        ) : (
          <>
            <Input label="Nome" value={name} onChange={(e) => setName(e.target.value)} />
            <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Input label="Telefone" value={phone} onChange={(e) => setPhone(formatPhoneMask(e.target.value))} />
            <Input label="Nova senha (opcional)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            {error ? <p className="text-sm font-medium text-red-500">{error}</p> : null}
            <div className="flex gap-2">
              <Button variant="primary" onClick={save} disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button variant="outline" onClick={logout}>
                Deslogar
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
